import { matchPath } from 'react-router';

import { Route } from 'src/shared/route';

const ALLOWED_HOSTS = /^(https:\/\/(\w+[.])*hover.to$)/;
const QUERY_STRING_REDIRECT_PARAMETER = 'redirect_to';
const QUERY_STRING_ORIGINAL_IMAGE_SHARE_PARAMETER = 'original_url';
const QUERY_STRING_GENERATED_IMAGE_SHARE_PARAMETER = 'url'; // leave as url for backward compatibility

/**
 * Delete search params from url and return the new url as a string
 *
 * @param url search part of the url, i.e. location.search
 * @returns params as a string "foo=1&bar=2" with the search param removed
 */
const getQueryParamsWithoutSearch = (url: string) => {
  const queryParams = new URLSearchParams(url);
  queryParams.delete('search');
  return queryParams.toString();
};

/**
 * Deletes all params except searchfrom url and return the new url as a string
 *
 * @param url search part of the url, i.e. location.search
 * @returns params as a string "search=foo" with all other params removed
 */
const getQueryParamsWithOnlySearch = (url: string) => {
  const queryParams = new URLSearchParams(url);
  const searchTerm = queryParams.get('search');
  return searchTerm ? `search=${searchTerm ?? ''}` : '';
};

/**
 * Extract redirect URL from a URL (usually the current location URL) with the
 * `?redirect_to=` query parameter.
 *
 * @param url URL to extract redirect param from
 * @returns redirect URL
 */
const getRedirectParam = (url: string) =>
  new URL('', url).searchParams.get(QUERY_STRING_REDIRECT_PARAMETER);

/**
 * Extract image share URL from a URL with the `?image_share=` parameter
 *
 * @param url URL to extract image share param from
 * @returns image share URL
 */
const getImageShareParam = (
  url: string,
  shareParam: string = QUERY_STRING_ORIGINAL_IMAGE_SHARE_PARAMETER,
) => new URL('', url).searchParams.get(shareParam);

/**
 * Validate that a URL is safe to redirect to by ensuring it either matches the
 * current host or a list of safe hosts.
 *
 * @param redirect redirect URL to validate
 * @param url current URL (used to determine current host)
 * @returns whether provided redirect URL is valid/safe
 */
const validateRedirectUrl = (redirect: string, url: string) => {
  const { origin } = new URL(url);
  const redirectOrigin = new URL(redirect, url).origin;

  return origin === redirectOrigin || ALLOWED_HOSTS.test(redirectOrigin);
};

const validateImageShareUrl = validateRedirectUrl;

/**
 * Get a *safe* redirect URL from the `?redirect_to=` query parameter falling
 * back to a default URL in the case of an invalid/unsafe redirect URL
 *
 * @param url URL including query string to extract redirect URL from (usually
 *            the current URL)
 * @param defaultRedirectPath URL to fall back to in case of invalid redirect URL
 * @returns
 */
const baseUrlToRedirectUrl = (
  url: string,
  defaultRedirectPath: string = Route.PROPERTIES.path,
) => {
  const redirectParam = getRedirectParam(url);
  const redirectTo =
    redirectParam && validateRedirectUrl(redirectParam, url)
      ? redirectParam
      : defaultRedirectPath;

  return new URL(redirectTo, url).href;
};

/**
 * Duplicates baseUrlToRedirectUrl but returns instance of URL instead of href string
 *
 * @param url URL including query string to extract redirect URL from (usually
 *            the current URL)
 * @param defaultRedirectPath URL to fall back to in case of invalid redirect URL
 * @returns URL
 */
const baseUrlToRedirect = (
  url: string,
  defaultRedirectPath: string = Route.PROPERTIES.path,
) => {
  const redirectParam = getRedirectParam(url);
  const redirectTo =
    redirectParam && validateRedirectUrl(redirectParam, url)
      ? redirectParam
      : defaultRedirectPath;

  return new URL(redirectTo, url);
};

/**
 * Get a *safe* image URL from the `?image_share=` query parameter. If the
 * query parameter does not exist or the image URL is not safe, an empty string
 * is returned
 *
 * @param url URL including query string to extract the image URL from
 * @returns safe URL or empty string
 */
const baseUrlToImageShareUrl = (url: string, shareParam?: string) => {
  const imageShareParam = getImageShareParam(url, shareParam);
  if (!imageShareParam || !validateImageShareUrl(imageShareParam, url)) {
    return '';
  }

  return new URL(imageShareParam, url).href;
};

/**
 * Get the post-SSO success URL given a current URL that may also include a
 * `?redirect_to=` URL param. Returns the internal post-SSO URL, preserving the
 * `?redirect_to=` parameter.
 *
 * @param url current URL
 * @returns post-SSO success URL with redirect param preserved
 */
const baseUrlToLoginSuccessUrl = (url: string) => {
  const redirectTo = getRedirectParam(url);
  const successUrl = new URL(Route.LOGIN_SSO_SUCCESS.path, url);

  if (redirectTo) {
    successUrl.searchParams.append(QUERY_STRING_REDIRECT_PARAMETER, redirectTo);
  }

  return successUrl.toString();
};

/**
 * Transfer query parameters from one URL to another
 *
 * @param newUrl new URL without query parameters
 * @param currentUrl current URL including query parameters
 * @returns new URL with query parameters from current URL
 */
const preserveQueryParameters = (newUrl: string, currentUrl: string) =>
  newUrl + new URL(currentUrl).search;

/**
 * Normalize URL `pathname` by stripping the trailing slash
 *
 * @param pathname pathname to normalize
 */
const normalizePathname = (pathname: string) => pathname.replace(/\/+$/, '');

const getRoute = (pathname: string) =>
  Object.values(Route).find(({ path }) => {
    const matches = matchPath(pathname, { path, strict: false, exact: true });
    return !!matches;
  });

/**
 * Check whether url provided is internal route availabe within web-react
 *
 * @param url URL including query string
 * @returns boolean
 */
const isInternalPath = (url: URL) => {
  const route = getRoute(url.pathname);
  return !!route;
};

/**
 * Given a `search` string from a URL, determine whether it contains the valid
 * parameters necessary for the Homeowner → Professional account upgrade path
 *
 * @param search URL `search` string
 *
 * @returns user ID for account to be upgraded for valid parameters, otherwise `null`
 */
const getUpgradeUserIdFromParams = (search: string) => {
  const params = new URLSearchParams(search);

  const userIdParam = params.get('user_id');
  const proParam = params.get('pro');

  const userId = userIdParam ? parseInt(userIdParam, 10) : null;
  const isUpgrade = proParam === 'true';

  return userId && isUpgrade ? userId : null;
};

/**
 * Constructs a URL object based on the given pathname.
 * If the code is running on the server, it uses 'https://hover.to' as the base URL.
 * If the code is running on the client, it uses the current window's origin as the base URL.
 *
 * @param pathname - The pathname to be appended to the base URL.
 * @returns A new URL object.
 */
const makeUrl = (pathname: string) =>
  new URL(
    pathname,
    typeof window === 'undefined' ? 'https://hover.to' : window.location.origin,
  );

export {
  QUERY_STRING_REDIRECT_PARAMETER,
  QUERY_STRING_ORIGINAL_IMAGE_SHARE_PARAMETER,
  QUERY_STRING_GENERATED_IMAGE_SHARE_PARAMETER,
  baseUrlToLoginSuccessUrl,
  baseUrlToImageShareUrl,
  baseUrlToRedirectUrl,
  baseUrlToRedirect,
  getRoute,
  getUpgradeUserIdFromParams,
  normalizePathname,
  preserveQueryParameters,
  isInternalPath,
  getQueryParamsWithoutSearch,
  getQueryParamsWithOnlySearch,
  makeUrl,
};
